
import FormatMixin from "~/mixin/format-mixin";
import { butter } from "../plugins/buttercms";
import ProductMapMixin from "~/mixin/product-map-mixin";
import ProductFetchMixin from "~/mixin/product-fetch-mixin";
import DeliveryWeekMixin from "~/mixin/delivery-week-mixin";
import Product from "~/types/Product";
import ShopifyProduct from "~/types/shopify/ShopifyProduct";
import CMSProduct from "~/types/cms/CMSProduct";
import {
  FilterBundleLandingProducts,
  invokeShopifyQuery,
} from "~/utils/shopify";

export default {
  name: "cal-dynamic-landing-products",
  props: ["componentData", "showHeader", "deliveryDate", "deliveryTown"],
  mixins: [FormatMixin, ProductMapMixin, ProductFetchMixin, DeliveryWeekMixin],
  async created() {
    await this.fetchProductDataList();
  },
  data() {
    return {
      dateCode: "",
      flowersList: [] as Product[],
      giftsetsList: [] as Product[],
      centrepiecesList: [] as Product[],
      curatedList: [] as Product[],
      driedFlowersList: [] as Product[],
      subsList: [] as Product[],
      landingDateCode: "",
    };
  },
  watch: {
    async deliveryDate() {
      if (this.deliveryDate !== "" && this.deliveryDate != null) {
        await this.fetchProductDataList();
      }
    },
    async deliveryTown() {
      await this.fetchProductDataList();
    },
  },
  methods: {
    async fetchProductDataList() {
      this.flowersList = [];
      this.giftsetsList = [];
      this.centrepiecesList = [];
      this.curatedList = [];
      this.driedFlowersList = [];
      this.subsList = [];
      const deliveryDate = sessionStorage.getItem("deliveryDate")
        ? sessionStorage.getItem("deliveryDate")
        : this.deliveryDate
        ? this.$dayjs(this.deliveryDate).format("YYYY-MM-DD")
        : this.$dayjs().format("YYYY-MM-DD");
      this.dateCode = this.selectWeekNumber(deliveryDate);
      this.landingDateCode = this.selectWeekNumber(
        this.$dayjs().format("YYYY-MM-DD")
      );
      let productData: CMSProduct[];
      productData = await this.getProductDataFromCMS();
      await this.getFlowersList(productData);
      await this.getGiftsetList(productData);
      await this.getCBCList(productData);
      await this.getSubscriptionList();
    },
    async getProductDataFromCMS() {
      try {
        const products = await butter(this.$config).content.retrieve(
          ["products"],
          {
            locale: this.$i18n.locale,
            timeout: 10000,
          }
        );
        return products?.data.data.products;
      } catch (error) {
        console.error(error);
      }
    },
    async getFlowersList(productData: CMSProduct[]) {
      try {
        this.flowersList = [];
        this.driedFlowersList = [];
        if (this.dateCode && this.isObjectNotEmpty(this.deliveryTown)) {
          await this.getBouquets({
            queryId: "products_with_product_type_filter",
            region: this.deliveryTown.regionShortCode,
            dateCode: this.dateCode,
          });
        } else if (this.isObjectNotEmpty(this.deliveryTown)) {
          await this.getBouquets({
            queryId: "products_with_product_type_filter",
            region: this.deliveryTown.regionShortCode,
            dateCode: this.landingDateCode,
          });
        } else {
          await this.getLandingBouquets(this.dateCode);
        }

        // create product list in session storage to keep list of available product names
        sessionStorage.productList = "";
        this.bouquetResponse.forEach((element: ShopifyProduct) => {
          let handle = element?.handle;
          if (handle) {
            sessionStorage.productList +=
              handle.substring(0, handle.indexOf("_")) + " ";
          }
          let product = new Product(element, productData);

          if (product.tags.includes("centrepiece")) {
            this.centrepiecesList.push(product);
          } else if (product.tags.includes("dried-bouquet")) {
            this.driedFlowersList.push(product);
          } else {
            this.flowersList.push(product);
          }
        });

        this.flowersList = this.sortProductsByStyle(this.flowersList);
        this.driedFlowersList = this.sortProductsByStyle(this.driedFlowersList);
      } catch (error) {
        console.error(error);
      }
    },
    async getGiftsetList(productData: CMSProduct[]) {
      try {
        if (this.dateCode && this.isObjectNotEmpty(this.deliveryTown)) {
          this.giftsetsList = [];
          await this.getGiftsets({
            queryId: "products_with_product_type_filter",
            region: this.deliveryTown.regionShortCode,
            dateCode: this.dateCode,
          });
        } else if (this.isObjectNotEmpty(this.deliveryTown)) {
          await this.getGiftsets({
            queryId: "products_with_product_type_filter",
            region: this.deliveryTown.regionShortCode,
            dateCode: this.landingDateCode,
          });
        } else {
          await this.getLandingGiftsets(this.landingDateCode);
        }

        this.giftsetResponse.forEach((element) => {
          let product = new Product(element, productData);
          this.giftsetsList.push(product);
        });

        this.giftsetsList.sort((a, b) => b.price - a.price);
      } catch (error) {
        console.error(error);
      }
    },
    async getSubscriptionList() {
      try {
        this.subsList = [];

        const prods = await butter(this.$config).content.retrieve(
          ["subscriptions"],
          {
            locale: this.$i18n.locale,
            timeout: 10000,
          }
        );

        let cmsProductData: CMSProduct[] = prods?.data.data.subscriptions;
        const locale = this.$i18n.locale;
        const countryCode = locale.split("-")[1];

        await this.getSubscriptions({
          queryId: "products_with_product_type_multi_week",
          region: countryCode,
        });
        this.subscriptionResponse.forEach((element) => {
          let product = new Product(element, cmsProductData);
          this.subsList.push(product);
        });
      } catch (error) {
        console.error(error);
      }
    },
    async getCBCList(productData: CMSProduct[]) {
      try {
        this.curatedList = [];
        const cbc = await invokeShopifyQuery(
          FilterBundleLandingProducts(this.dateCode, "Curated_Bundle")
        );

        let curatedList: ShopifyProduct[] = this.mapShopifyProducts(cbc.data);

        const curatedListByTitle: { [key: string]: ShopifyProduct } = {};

        for (let item of curatedList) {
          if (
            !(curatedListByTitle[item.title] && item.handle?.includes("_ca"))
          ) {
            curatedListByTitle[item.title] = item;
          }
        }

        curatedList = Object.values(curatedListByTitle);

        curatedList.forEach((element) => {
          let product = new Product(element, productData);

          this.curatedList.push(product);
        });

        this.curatedList.sort(
          (a: { price: number }, b: { price: number }) => b.price - a.price
        );
      } catch (error) {
        console.error(error);
      }
    },
    isObjectNotEmpty(obj: any) {
      return Object.keys(obj).length > 0;
    },
  },
};
